import React, { useEffect, useRef, useState } from "react";
import { useReveal } from "../hooks/useReveal";

const VIDEO_SRC = "/assets/protos-liquid-core.mp4";

const PILLARS = [
  {
    n: "01",
    label: "Resistance Chewing",
    body: "Engineered to anchor attention and create a rhythmic, grounding effect.",
  },
  {
    n: "02",
    label: "Trigeminal Activation",
    body: "Engages the nerve system that drives alertness and sensory awareness.",
  },
  {
    n: "03",
    label: "Clean Focus Stack",
    body: "Three actives chosen to support, not overpower, the chewing experience.",
  },
];

/* ─────────────────────────────────────────────────────────
 * LiquidCoreReveal — cinematic video block.
 * Autoplays muted on enter, pauses when out of view, plays on hover
 * (desktop) and on intersection (mobile). Frame is a black plate with
 * a glowing blue rim + scan-line, designed to make people pause.
 * ────────────────────────────────────────────────────────*/
function LiquidCoreReveal() {
  const wrapRef = useReveal();
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      data-testid="liquid-core-reveal"
      className="reveal relative mt-16 lg:mt-24"
    >
      {/* Section label */}
      <div className="flex items-end justify-between mb-5 lg:mb-7 gap-4">
        <div>
          <div className="font-mono text-[10px] lg:text-xs uppercase tracking-[0.3em] text-white/45">
            / Sequence · 001
          </div>
          <h3
            className="kinetic mt-3"
            style={{ fontSize: "clamp(1.6rem, 3.6vw, 3rem)", lineHeight: 0.95 }}
          >
            <span className="gradient-text">LIQUID CORE.</span>{" "}
            <span className="outline-text-blue">EUCALYPTUS MINT.</span>
          </h3>
        </div>
        <div className="hidden md:flex items-center gap-2 shrink-0 pb-2">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{
              background: "var(--blue-bright)",
              boxShadow: "0 0 10px var(--blue-bright)",
              animation: "pulse 1.6s ease-in-out infinite",
            }}
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/55">
            Engineered
          </span>
        </div>
      </div>

      {/* Cinema frame */}
      <div className="relative group">
        {/* outer glow */}
        <div
          aria-hidden
          className="absolute -inset-6 rounded-[28px] blur-3xl opacity-50 pointer-events-none transition-opacity duration-700 group-hover:opacity-90"
          style={{
            background:
              "radial-gradient(circle at 30% 50%, rgba(31,179,255,0.45), transparent 60%), radial-gradient(circle at 80% 50%, rgba(63,226,255,0.3), transparent 65%)",
          }}
        />

        {/* frame */}
        <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden border border-white/10 bg-black">
          {/* corner brackets */}
          {[
            "top-3 left-3 border-t border-l",
            "top-3 right-3 border-t border-r",
            "bottom-3 left-3 border-b border-l",
            "bottom-3 right-3 border-b border-r",
          ].map((cls, i) => (
            <span
              key={i}
              aria-hidden
              className={`absolute z-20 w-5 h-5 ${cls}`}
              style={{ borderColor: "rgba(63,226,255,0.55)" }}
            />
          ))}

          <video
            ref={videoRef}
            data-testid="liquid-core-video"
            src={VIDEO_SRC}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onLoadedData={() => setReady(true)}
            className={`block w-full h-auto aspect-video object-cover transition-opacity duration-700 ${
              ready ? "opacity-100" : "opacity-0"
            }`}
          />

          {/* Subtle scan line overlay */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-40"
            style={{
              background:
                "repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 4px)",
            }}
          />

          {/* Vignette */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at center, transparent 50%, rgba(0,0,0,0.55) 100%)",
            }}
          />

          {/* Loading placeholder */}
          {!ready && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{
                  background: "var(--blue-bright)",
                  boxShadow: "0 0 12px var(--blue-bright)",
                  animation: "pulse 1.4s ease-in-out infinite",
                }}
              />
            </div>
          )}

          {/* Bottom HUD strip */}
          <div className="absolute bottom-0 left-0 right-0 z-20 p-4 lg:p-6 flex items-end justify-between gap-3 bg-gradient-to-t from-black/85 via-black/30 to-transparent">
            <div className="font-mono text-[9px] lg:text-[10px] uppercase tracking-[0.3em] text-white/65">
              REC · 90s ENGAGEMENT · BATCH 001
            </div>
            <div className="font-mono text-[9px] lg:text-[10px] uppercase tracking-[0.3em]" style={{ color: "var(--blue-bright)" }}>
              PROTOS · LIVE
            </div>
          </div>

          {/* Top side legend (desktop only) */}
          <div className="hidden lg:flex absolute top-5 left-0 right-0 z-20 px-6 items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
            <span>Sample · 001</span>
            <span>Liquid center · Mint</span>
          </div>
        </div>
      </div>

      {/* Caption strip below frame */}
      <div className="mt-5 lg:mt-7 grid sm:grid-cols-3 gap-3 lg:gap-6">
        {[
          ["Build", "Resistance shell + liquid core"],
          ["Flavor", "Eucalyptus mint, long-form"],
          ["Intent", "A ritual you can repeat"],
        ].map(([k, v], i) => (
          <div
            key={i}
            data-testid={`liquid-caption-${i}`}
            className="flex items-baseline gap-3 py-2"
          >
            <span
              className="font-mono text-[10px] uppercase tracking-[0.3em] shrink-0"
              style={{ color: "var(--blue-bright)" }}
            >
              / 0{i + 1}
            </span>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
                {k}
              </div>
              <div className="text-white/85 text-sm lg:text-base mt-1 leading-snug">
                {v}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WhatIsProtos() {
  const head = useReveal();
  const body = useReveal();
  return (
    <section
      id="what"
      data-testid="what-section"
      className="section relative overflow-hidden"
    >
      <div className="container-x relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div ref={head} className="reveal lg:col-span-4">
            <span className="font-mono text-sm uppercase tracking-[0.3em] text-white/55">
              / 02 — The Product
            </span>
            <h2
              className="kinetic mt-6"
              style={{ fontSize: "clamp(2.6rem, 6vw, 5.4rem)", lineHeight: 0.95 }}
            >
              <span className="gradient-text">WHAT IS</span>
              <br />
              <span className="outline-text-blue">PROTOS?</span>
            </h2>
          </div>

          <div ref={body} className="reveal lg:col-span-8 space-y-10">
            <p className="text-xl lg:text-2xl text-white font-light leading-snug">
              A performance chew designed for{" "}
              <span style={{ color: "var(--blue-bright)" }}>state control</span>.
              Built to help you become more focused, calm, and intentional during
              the moments that matter.
            </p>

            <div className="grid sm:grid-cols-3 gap-4">
              {PILLARS.map((p, i) => (
                <div
                  key={i}
                  data-testid={`what-pillar-${i}`}
                  className="card-dark p-5"
                >
                  <div
                    className="font-mono text-[10px] uppercase tracking-[0.25em] mb-3"
                    style={{ color: "var(--blue-bright)" }}
                  >
                    / {p.n}
                  </div>
                  <div className="font-display text-lg uppercase text-white mb-2">
                    {p.label}
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>

            <p className="text-white/55 text-base leading-relaxed pt-2">
              The goal isn&apos;t overstimulation.{" "}
              <span className="text-white">The goal is controlled focus.</span>
            </p>
          </div>
        </div>

        {/* Cinematic product video reveal */}
        <LiquidCoreReveal />
      </div>
    </section>
  );
}
